import { mergeDeepRight } from 'ramda'

import { initialState, types } from './index'
import { types as CareTypes } from 'store/Care'
import { types as DoctorTypes } from 'store/Doctor'
import { types as ExpenseTypes } from 'store/Expense'
import { types as TrasactionTypes } from 'store/Subscription'

export const updateSearchFilters = filters => ({
  type: types.UPDATE_SEARCH_FILTERS,
  payload: filters,
})

export const resetSearchFilters = () => ({
  type: types.RESET_SEARCH_FILTERS,
})

export const deleteSearchFilter = key => ({
  type: types.DELETE_SEARCH_FILTER,
  payload: key,
})

export const resetSearchOptions = () => ({
  type: types.RESET_SEARCH_OPTIONS,
})

export default (state = initialState, { type, payload = {} } = {}) => {
  switch (type) {
    case types.UPDATE_SEARCH_FILTERS: {
      const {
        searchLocation,
        careTypes,
        priceRange,
        searchingAs,
        careName,
        careSearchQuery,
        specialty,
        ...otherFilters
      } = payload
      const stateFilters = { ...state.filters }

      // Special use case for prescriptions, we don't want to send the gsn value to ScriptSave
      // on the brand filter change, remove the gsn value from the queryMap before it sent to
      if (
        // Check if the filter is related to prescriptions
        stateFilters?.category === 'prescriptions' &&
        // Check if the gsn value is present in the initial queryMap
        !!stateFilters?.queryMap?.gsn &&
        // only trigger the delete of the gsn if the brand has been changed by
        // checking if the drugName value is different from the prev state filter
        otherFilters?.queryMap?.drugName !== stateFilters?.queryMap?.drugName
      ) {
        // Remove the gsn value from the prev state queryMap, it is not passed in by brand
        delete stateFilters.queryMap.gsn
      }

      //TODO I don't love this, really the different kinds of SearchFilter updates should be handled by separate functions
      //TODO but I'm not sure how to do that without a lot of refactoring, and we're getting rid of this entire store soon ideally
      if (payload.careName || payload.careSearchQuery || payload.specialty) {
        delete stateFilters.careName
        delete stateFilters.careSearchQuery
        delete stateFilters.specialty
      }

      //TODO This is a terrible assumption about a search tool, keeping data from previous searches is almost never a good idea.
      //TODO Need to figure out the implications of removing this ASAP, since it's going to be a problem with every smart layer enhancement moving forward.
      // most fields can be merged in to not be disruptive,
      let nextState = {
        ...state,
        filters: mergeDeepRight(stateFilters, otherFilters),
      }

      // some need to be updated with setIn or else the old values don't die.
      // these are typically the array ones which means this could be done better.
      if (careTypes) {
        nextState = {
          ...nextState,
          filters: {
            ...nextState.filters,
            careTypes: careTypes,
          },
        }
      }
      if (priceRange) {
        nextState = {
          ...nextState,
          filters: {
            ...nextState.filters,
            priceRange: priceRange,
          },
        }
      }
      if (searchingAs) {
        nextState = {
          ...nextState,
          filters: {
            ...nextState.filters,
            searchingAs: searchingAs,
          },
        }
      }
      if (careName) {
        nextState = {
          ...nextState,
          filters: {
            ...nextState.filters,
            careName: careName,
          },
        }
      }
      if (careSearchQuery) {
        nextState = {
          ...nextState,
          filters: {
            ...nextState.filters,
            careSearchQuery: careSearchQuery,
          },
        }
      }
      if (specialty) {
        nextState = {
          ...nextState,
          filters: {
            ...nextState.filters,
            specialty: specialty,
          },
        }
      }

      const filters = nextState?.filters
      const location = filters?.searchLocation

      const activeFilters = nextState?.activeFilters
      const activeLocation = activeFilters?.searchLocation

      // For HIPAA reasons, only the address field returned from the BE for dependents is the state
      // This causes fetchCareDetail requests to fail and the care details to error out
      // as a temp solution, we are using the primary's address (activeLocation) when searching as a dependent
      if (!searchLocation && location && activeLocation) {
        // check if there is a zip code, if not, set in the activeLocation
        if (!location.zip) {
          return {
            ...nextState,
            filters: {
              ...nextState.filters,
              searchLocation: activeLocation,
            },
          }
        }
      }

      // when we update the searchLocation we want to make sure
      // that we completely replace what was there, BUT ONLY IF
      // search location is not empty, otherwise our effort to
      // dance around searchLocation is wasted.
      return !searchLocation
        ? nextState
        : {
            ...nextState,
            filters: {
              ...nextState.filters,
              searchLocation: searchLocation,
            },
          }
    }

    case types.RESET_SEARCH_FILTERS: {
      // these filters are 'protected' because they are useful once set,
      // we pull them out before resetting, then put them back in.
      const { searchLocation, searchingAs } = state.filters

      return {
        ...state,
        filters: {
          searchLocation,
          searchingAs,
        },
      }
    }

    case types.DELETE_SEARCH_FILTER: {
      const { [payload]: _, ...remainingFilters } = state.filters
      return {
        ...state,
        filters: remainingFilters,
      }
    }

    case types.RESET_SEARCH_OPTIONS: {
      return {
        ...state,
        filterOptions: initialState.filterOptions,
      }
    }

    case CareTypes.FETCH_DOCTOR_PRICING_SUCCESS:
    case CareTypes.FETCH_CARE_SEARCH_SUCCESS:
    case CareTypes.FETCH_RX_PRICING_SUCCESS:
    case CareTypes.FETCH_PRESCRIPTIONS_SUCCESS:
    case CareTypes.FETCH_PUBLIC_CARE_SEARCH_SUCCESS:
    case CareTypes.FETCH_PUBLIC_CARE_DETAIL_SUCCESS:
    case DoctorTypes.FETCH_DOCTORS_SUCCESS:
    case DoctorTypes.FETCH_SEARCH_SUGGESTIONS_SUCCESS:
    case DoctorTypes.FETCH_DOCTOR_REVIEWS_SUCCESS:
    case DoctorTypes.SELECT_DOCTOR_SUCCESS:
    case DoctorTypes.FETCH_DOCTOR_PROFILE_SUCCESS:
    case DoctorTypes.FETCH_DOCTOR_COSTS_SUCCESS:
    case ExpenseTypes.FETCH_EXPENSES_SUCCESS:
    case ExpenseTypes.FETCH_EXPENSE_DETAILS_SUCCESS:
    case ExpenseTypes.FETCH_EXPENSE_CHART_SUCCESS:
    case TrasactionTypes.FETCH_BILLING_HISTORY_SUCCESS: {
      return {
        ...state,
        activeFilters: state.filters,
      }
    }

    default:
      return state
  }
}
